import EditRounded from "@mui/icons-material/EditRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, CardContent, Grid, MenuItem, Theme, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { IFullFlight } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { requestFlightPairingStatusRecompute } from "src/API/FlightPairingStatusV2";
import ContextMenu from "src/components/ContextMenu/ContextMenu";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { FlightChip, IFlightChip } from "src/components/Flights/FlightChip";
import { IconButton } from "src/components/IconButton/IconButton";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { selectFlightPairingStatus } from "src/redux/actions/FlightPairingStatusV2/FlightPairingStatusV2.Actions";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { v1 } from "uuid";
import { IFlightPairingStatusActualCrew, IFlightPairingStatusRow } from "./pairingStatusTypes";

interface IFlightPairingStatusV2ContextProps {
  id: string;
  path: string[];
  setActiveEventModal: (activeEventModal: EventModalTypes) => void;
}

const statusBadge = (theme: Theme): object => ({
  display: "inline-block",
  textAlign: "center",
  margin: "2px 5px",
  color: theme.palette.common.black,
  fontSize: 10,
  border: `1px solid ${theme.palette.background.default}`,
  borderRadius: 3,
  padding: "2px 8px",
  fontWeight: 700,
  "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
  "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
  "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
  "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
  "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
  "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },
});

const OCContextMenu = styled(ContextMenu)(({ theme }) => ({
  "& .MuiCard-root": { height: "100%" },
  "& .edit_flight_btn": {
    width: 20,
    height: 20,
    padding: 0,
    margin: 5,
    borderRadius: 3,
    border: "1px solid",
    "& .MuiSvgIcon-root": { width: 14, height: 14, borderRadius: 3, margin: "0px 5px" },
  },
  "& .flight_info": {
    "& .flightchip": { marginBottom: 10 },
    "& .info": { margin: "2px 5px", fontSize: 12 },
    "& .info_row": { display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap" },
    "& .status": statusBadge(theme),
    "& .status_message": { margin: "0px 5px 6px 5px", fontSize: 11, color: theme.custom.forms.labels },
  },
  "& .pairing_info": {
    padding: "10px 16px",
    margin: "0px 0px 10px 0px",
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& .info": { margin: "2px 5px", fontSize: 12 },
    "& .pairing_positions": { display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center" },
    "& .crew_badge": { justifyContent: "center" },
    "& li.MuiListItem-root": { margin: "0px -15px" },
  },
  "& .actions": { display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 6, padding: "0px 16px 10px 16px" },
}));

const positionLabel = (position: { positionCode: string; count: number }): string =>
  position.count > 1 ? `${position.positionCode} x${position.count}` : position.positionCode;

/** Human wording for the cache's compute reason codes. */
const COMPUTE_REASON_LABELS: Record<string, string> = {
  scheduled: "scheduled refresh",
  manual: "manual recompute",
  onDemand: "evaluated when opened",
  crewChange: "crew change",
  pairingChange: "pairing change",
  flightChange: "flight change",
};

/**
 * Detail panel for the selected flight: the required crew, the pairings and crew actually on the flight, and
 * the shortcuts to fix a problem (edit the flight, open a pairing, add the flight to a pairing).
 */
const FlightPairingStatusV2ContextMenu = (props: IFlightPairingStatusV2ContextProps): JSX.Element => {
  const { id, path, setActiveEventModal } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const allMenus = useSelector((state: IRootState) => state.contextMenu.menus);
  const row: IFlightPairingStatusRow | undefined = useSelector((state: IRootState) => {
    const selected = state.flightPairingStatusV2.selectedFlightId;
    return selected ? state.flightPairingStatusV2.rows[selected] : undefined;
  });
  const [isRecomputing, setIsRecomputing] = useState<boolean>(false);

  const open = useMemo(() => allMenus.get(id)?.isOpen ?? false, [allMenus, id]);

  // Crew grouped by pairing, in the order the pairings were attached to the flight.
  const crewByPairing = useMemo(() => {
    const map = new Map<string, { shortId: string; crew: IFlightPairingStatusActualCrew[] }>();
    for (const pairing of row?.pairings ?? []) map.set(pairing._id, { shortId: pairing.shortId, crew: [] });
    for (const crew of row?.actualCrew ?? []) {
      const entry = map.get(crew.pairing) ?? { shortId: crew.prgCode, crew: [] };
      entry.crew.push(crew);
      map.set(crew.pairing, entry);
    }
    return [...map.entries()].map(([pairingId, entry]) => ({ pairingId, ...entry }));
  }, [row]);

  if (!row?.flightId) return null;

  const flightId = row.flightId;
  const missingByPosition = new Map((row.crewSummary?.missing ?? []).map((m) => [m.positionCode, m.count]));
  const departure = row.actualOut ?? row.scheduledOut;

  const flightChip: IFlightChip = {
    _id: flightId,
    flightNumber: row.flightNumber,
    flightNumberSuffix: row.flightNumberSuffix ?? undefined,
    origin: row.origin,
    destination: row.destination,
    flightType: row.flightType
      ? ({
          _id: row.flightType._id,
          title: row.flightType.title,
          color: row.flightType.color,
        } as IFlightChip["flightType"])
      : undefined,
  };

  const editFlight = (): void => {
    dispatch(closeContextMenu(id));
    dispatch(
      openWindow(
        "Flight Editor",
        "BetaEventEditor",
        path,
        { eventId: flightId, isFlight: true, aircraftId: row.aircraft?.registration },
        "context"
      )
    );
  };

  // The cache row is a reduced view of the flight; the pairing editor needs the full document.
  const createNewPairing = async (): Promise<void> => {
    const fullFlight = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${flightId}`);
    if (fullFlight?._id) {
      dispatch(
        openWindow(
          "New Pairing",
          "PairingEditorWindow",
          path,
          { pairingId: `new_${v1()}`, flights: [fullFlight] },
          null
        )
      );
    }
  };

  const assignFlightsToPairing = async (): Promise<void> => {
    const fullFlight = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${flightId}`);
    if (fullFlight?._id) {
      dispatch(setSelectedEvents([fullFlight]));
      setActiveEventModal(EventModalTypes.AssignToPairing);
    }
  };

  // Re-evaluate just this flight; the updated document arrives through the websocket update.
  const recomputeFlight = async (): Promise<void> => {
    setIsRecomputing(true);
    try {
      const result = await requestFlightPairingStatusRecompute({ flightIds: [flightId] }, dispatch);
      if (result) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "info",
            message:
              result.removed > 0
                ? "Flight no longer exists - removed from the status list"
                : result.changed > 0
                  ? "Re-evaluated: status updated"
                  : "Re-evaluated: no change",
          })
        );
      }
    } finally {
      setIsRecomputing(false);
    }
  };

  const closeDrawer = (): void => {
    dispatch(closeContextMenu(id));
    dispatch(selectFlightPairingStatus(null));
  };

  return (
    <OCContextMenu
      key={`${id}-contextMenu`}
      id={id}
      menuHeader="Flight Pairing Status v2"
      isOpen={open}
      onClose={closeDrawer}
      theme={theme}
    >
      <Grid container direction="column">
        <CardContent className="flight_info">
          <Grid container direction="row" alignItems="center">
            <FlightChip className="flightchip" data={flightChip} />
            <Tooltip title="Edit flight">
              <IconButton onClick={editFlight} className="edit_flight_btn" size="large">
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title="Re-evaluate this flight now">
              <span>
                <IconButton onClick={recomputeFlight} className="edit_flight_btn" size="large" disabled={isRecomputing}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <div className="info_row">
            <Typography className="info" variant="body2">
              Status:
            </Typography>
            <span className={`status ${row.status?.className ?? ""}`}>{row.status?.label}</span>
          </div>
          <Typography className="status_message" variant="body2">
            {row.status?.message}
          </Typography>

          <div className="info_row">
            <Typography className="info" variant="body2">
              Aircraft:
            </Typography>
            <Typography className="info" variant="body2" color="textPrimary">
              {row.aircraft?.registration ?? "Unassigned"}
              {row.aircraft?.planning ? " (planning)" : ""}
              {row.aircraft?.subCharter ? " (sub charter)" : ""}
            </Typography>
          </div>
          <div className="info_row">
            <Typography className="info" variant="body2">
              Aircraft Config:
            </Typography>
            <Typography className="info" variant="body2" color="textPrimary">
              {row.aircraftConfiguration?.configTitle ?? "Unknown"}
            </Typography>
          </div>
          <div className="info_row">
            <Typography className="info" variant="body2">
              Departure:
            </Typography>
            <Typography className="info" variant="body2" color="textPrimary">
              {departure ? `${moment.utc(departure).format("ddd DD MMM YYYY HH:mm")}z` : "Not Set"}
            </Typography>
          </div>
          <div className="info_row">
            <Typography className="info" variant="body2">
              Required Crew:
            </Typography>
            {row.desiredCrew?.length > 0 ? (
              row.desiredCrew.map((d) => (
                <PositionBadge
                  className="crew_badge"
                  key={d.positionCode}
                  positionTitle={d.positionCode}
                  count={d.count}
                  filled={!missingByPosition.has(d.positionCode)}
                />
              ))
            ) : (
              <Typography className="info" variant="body2" color="textPrimary">
                Unknown
              </Typography>
            )}
          </div>
          {row.crewSummary && (
            <div className="info_row">
              <Typography className="info" variant="body2">
                Assigned:
              </Typography>
              <Typography className="info" variant="body2" color="textPrimary">
                {row.crewSummary.assigned}/{row.crewSummary.required}
                {row.crewSummary.missing?.length > 0 &&
                  ` · missing ${row.crewSummary.missing.map(positionLabel).join(", ")}`}
                {row.crewSummary.extraCrew?.length > 0 &&
                  ` · extra ${row.crewSummary.extraCrew.map(positionLabel).join(", ")}`}
                {row.crewSummary.extraPositions?.length > 0 &&
                  ` · extra position ${row.crewSummary.extraPositions.map(positionLabel).join(", ")}`}
              </Typography>
            </div>
          )}
          <div className="info_row">
            <Typography className="info" variant="body2">
              Evaluated:
            </Typography>
            <Typography className="info" variant="body2" color="textPrimary">
              {row.computedAt
                ? `${moment.utc(row.computedAt).format("DD MMM HH:mm:ss")}z (${COMPUTE_REASON_LABELS[row.computeReason] ?? row.computeReason})`
                : "-"}
            </Typography>
          </div>
        </CardContent>

        <Typography className="info pairings" variant="body2" style={{ fontSize: 12, margin: "5px 21px" }}>
          Pairings:
        </Typography>
        {crewByPairing.length > 0 ? (
          crewByPairing.map((pairing) => (
            <CardContent key={pairing.pairingId} className="pairing_info">
              <Grid container direction="row" alignItems="center">
                <ShortCodeChip code={pairing.shortId || "?"} itemId={pairing.pairingId} type="pairing" edit={true} />
                <Typography className="info" variant="body2" style={{ fontSize: 12, margin: 5 }}>
                  Positions:
                </Typography>
                <div className="pairing_positions">
                  {pairing.crew.map((c, index) => (
                    <Tooltip
                      key={`${pairing.pairingId}-${index}`}
                      title={
                        c.overridden
                          ? `Position overridden to ${c.pos} on this flight`
                          : c.crew
                            ? `${c.pos} filled`
                            : `${c.pos} open`
                      }
                    >
                      <div style={{ marginLeft: 5 }}>
                        <PositionBadge
                          className="crew_badge"
                          positionTitle={c.overridden ? `${c.pos}*` : c.pos}
                          filled={!!c.crew}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </Grid>
            </CardContent>
          ))
        ) : (
          <CardContent className="pairing_info">
            <MenuItem onClick={createNewPairing}>Add Flight to New Pairing</MenuItem>
            <MenuItem onClick={assignFlightsToPairing}>Add Flight to Existing Pairing</MenuItem>
          </CardContent>
        )}
        {crewByPairing.length > 0 && (
          <div className="actions">
            <Button size="small" variant="outlined" onClick={assignFlightsToPairing}>
              Add to another pairing
            </Button>
          </div>
        )}
      </Grid>
    </OCContextMenu>
  );
};

export default FlightPairingStatusV2ContextMenu;
